import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Shimmer, ChartShimmer } from "@/components/ui/shimmer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useChartColors } from "@/hooks/useChartColors";
import type { GiftCardPurchase } from "@/utils/dashboard-api";
import { getServiceName, getServiceImage } from "@/utils/dashboard-api";

interface GiftCardChartProps {
  purchases: GiftCardPurchase[];
  loading: boolean;
}

interface ServiceAgg {
  name: string;
  image: string;
  totalAmount: number;
  totalDiscounted: number;
  count: number;
  savings: number;
}

export default function GiftCardChart({ purchases, loading }: GiftCardChartProps) {
  const colors = useChartColors();

  const aggregated = useMemo(() => {
    const map = new Map<string, ServiceAgg>();

    for (const p of purchases) {
      const name = getServiceName(p);
      const image = getServiceImage(p);
      const entry = map.get(name) ?? {
        name,
        image,
        totalAmount: 0,
        totalDiscounted: 0,
        count: 0,
        savings: 0,
      };
      entry.totalAmount += p.amount;
      entry.totalDiscounted += p.discounted_amount;
      entry.count += 1;
      entry.savings += p.amount - p.discounted_amount;
      map.set(name, entry);
    }

    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [purchases]);

  const chartData = aggregated.slice(0, 8).map((s) => ({
    name: s.name.length > 12 ? s.name.slice(0, 12) + "…" : s.name,
    faceValue: +(s.totalAmount / 100).toFixed(2),
    paid: +(s.totalDiscounted / 100).toFixed(2),
  }));

  return (
    <Shimmer loading={loading} fallback={<ChartShimmer height={280} />}>
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-semibold">Gift Card Purchases</h3>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                Spend by brand &mdash; face value vs discounted price
              </p>
            </div>
          </div>

          {aggregated.length === 0 ? (
          <div className="flex items-center justify-center h-[280px]">
            <span className="text-sm text-muted-foreground">No gift card purchases in this period</span>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, bottom: 5, left: 0, right: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    fontSize: "13px",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "13px" }} />
                <Bar dataKey="faceValue" name="Face Value" fill={colors.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="paid" name="Paid" fill={colors.secondary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Brand</TableHead>
                    <TableHead className="font-semibold text-right">Orders</TableHead>
                    <TableHead className="font-semibold text-right">Face Value</TableHead>
                    <TableHead className="font-semibold text-right">Paid</TableHead>
                    <TableHead className="font-semibold text-right">Savings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aggregated.map((s) => (
                    <TableRow key={s.name}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-6 w-6 rounded">
                            <AvatarImage src={s.image} />
                            <AvatarFallback className="text-[10px] rounded">{s.name[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{s.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{s.count}</TableCell>
                      <TableCell className="text-right text-sm">
                        ₹{(s.totalAmount / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        ₹{(s.totalDiscounted / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-green-600 dark:text-green-400">
                        ₹{(s.savings / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
      </Card>
    </Shimmer>
  );
}
